import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Json, JsonObject } from '@se/cp.common';
import { CpUcConstraintFunctionsService } from '@se/cp.usercontrols/cp-uc-constraints-custom-functions/cp-uc-constraint-functions.service';
import { EstensaWaitingService, PatientReportHistoryService, ReportingDataBuilder } from '@se/platform';
import { SeConstraintCheckerService } from '@se/se.platform.base';
import { firstValueFrom, Subscription } from 'rxjs';
import { delay, finalize, map, tap } from 'rxjs/operators';

import { CpAdditionalSheetsService } from './cp-additional-sheets.service';
import { CpDatasetService } from './cp-dataset-service';
import { CpDatasourceService } from './cp-datasource.service';
import { CpModelService } from './cp-model.service';
import { CpNodeService } from './cp-node.service';
import { CpReportService } from './cp-report.service';
import { cpInitialize, cpSafeExit } from './store/cp.actions';
import * as fromCp from './store/cp.state';

@Injectable({
    providedIn: 'root',
})
export class CpUiEngineService {
    private imagingSubscription?: Subscription;

    constructor(
        private constraintChecker: SeConstraintCheckerService,
        private constraintFunctionsService: CpUcConstraintFunctionsService,
        private datasetService: CpDatasetService,
        private dataSourceService: CpDatasourceService,
        private modelService: CpModelService,
        private nodeService: CpNodeService,
        private reportService: CpReportService,
        private additionalSheetsService: CpAdditionalSheetsService,
        private reportingDataBuilder: ReportingDataBuilder,
        private store: Store<fromCp.RootState>,
        private waitingService: EstensaWaitingService,
        private translateService: TranslateService,
        private patientReportHistoryService: PatientReportHistoryService,
    ) {}

    async initCustomPage(idExaminations?: string[], idCustomPageRoot?: string) {
        return firstValueFrom(
            this.reportingDataBuilder.getMasterDataResolver().getMasterData(idExaminations, idCustomPageRoot),
        ).then(({ constraints, dataset, dataSources, idCustomPage, idModelTypes, layout, model, placeholders }) => {
            this.datasetService.initialize(dataset);
            this.dataSourceService.setDataSources(dataSources);
            this.constraintFunctionsService.init(
                this.modelService,
                this.nodeService,
                this.dataSourceService,
                this.translateService,
            );
            this.constraintChecker.addCustomFunctions(this.constraintFunctionsService.functionsEntries);

            this.modelService.initialize(model, constraints, placeholders);

            this.reportService.initDefaultValues(model, layout.autoNormalValues);
            this.nodeService.initialize(layout);
            this.additionalSheetsService.initialize(layout.additionalSheets ?? []);
            this.patientReportHistoryService.setCpSources(layout.patientHistoryActors ?? []);
            this.store.dispatch(
                cpInitialize({
                    page: {
                        idCustomPage,
                        idCustomPageRoot: this.reportingDataBuilder.getMasterDataResolver().getCustomPageRoot(),
                        idModelTypes,
                        modules: layout.modules || [],
                        sideComponents: layout.seSideComponents ?? [],
                        leftSliderClosed: Boolean(layout.leftSliderClosed),
                        rightSliderClosed: Boolean(layout.rightSliderClosed),
                        rightSliderEnabled: Boolean(layout.rightSliderEnabled),
                        imaging: layout.imaging,
                        actors: layout.patientHistoryActors ?? [],
                    },
                    toolbar: this.buildToolbarTitle(model),
                    isPreview: model.IsPreview as boolean,
                }),
            );
        });
    }

    private buildToolbarTitle(model: JsonObject) {
        if (!this.isValidReport(model.Report)) {
            return {};
        }
        const examinations = model.Report.Examinations;

        const descriptions: string[] = [];
        for (const examination of examinations) {
            if (!this.isFormattableExamination(examination)) {
                continue;
            }

            const examDescription = examination.IdExam.Description;
            if (examDescription?.toLowerCase().startsWith('ecg')) {
                // per ovviare al problema di "Visita+ECG" / "ECG+Visita" // non ho altro modo per ora
                // GM: Copiato da vecchio codice, non dare la colpa a me
                descriptions.push(examDescription);
            } else if (examDescription) {
                descriptions.unshift(examDescription);
            }
        }

        return { description1: descriptions.join(' + ') };
    }

    private isValidReport(report: Json): report is { Examinations: Json[] } {
        return (
            !!report &&
            typeof report === 'object' &&
            'Examinations' in report &&
            !!report.Examinations &&
            report.Examinations instanceof Array
        );
    }

    private isFormattableExamination(examination: Json): examination is { IdExam: { Description: string } } {
        return (
            !!examination &&
            typeof examination === 'object' &&
            'IdExam' in examination &&
            !!examination.IdExam &&
            typeof examination.IdExam === 'object' &&
            'Description' in examination.IdExam &&
            typeof examination.IdExam.Description === 'string'
        );
    }

    async changeLayout(idCustomPage: string) {
        this.waitingService.isWaiting = true;

        return firstValueFrom(
            this.reportingDataBuilder
                .getMasterDataResolver()
                .getLayout(idCustomPage)
                .pipe(
                    finalize(() => (this.waitingService.isWaiting = false)),
                    map(({ constraints, dataSources, idModelTypes, layout }) => {
                        this.dataSourceService.setDataSources(dataSources);
                        this.modelService.initializeConstraints(constraints);

                        this.nodeService.safeExit();

                        return { idModelTypes, layout };
                    }),
                    delay(1),
                    tap(({ idModelTypes, layout }) => {
                        this.nodeService.initialize(layout);
                        this.patientReportHistoryService.setCpSources(layout.patientHistoryActors ?? []);
                        this.store.dispatch(
                            cpInitialize({
                                page: {
                                    idCustomPage,
                                    idCustomPageRoot: this.reportingDataBuilder
                                        .getMasterDataResolver()
                                        .getCustomPageRoot(),
                                    idModelTypes,
                                    modules: layout.modules,
                                    sideComponents: layout.seSideComponents ?? [],
                                    rightSliderClosed: Boolean(layout.rightSliderClosed),
                                    leftSliderClosed: Boolean(layout.leftSliderClosed),
                                    rightSliderEnabled: Boolean(layout.rightSliderEnabled),
                                    imaging: layout.imaging,
                                    actors: layout.patientHistoryActors ?? [],
                                },
                            }),
                        );
                    }),
                ),
        );
    }

    safeExit() {
        this.store.dispatch(cpSafeExit());
        this.imagingSubscription?.unsubscribe();
    }
}
